import { Search, Compass, Mic, Flame, Sparkles } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { slateCardClass, primaryCtaClass } from "@/lib/card-styles";
import { cn } from "@/lib/utils";

interface HomeEmptyStateProps {
    isAuthenticated: boolean;
    onBrowseTopics?: () => void;
    onStartPractice?: () => void;
    onSignIn?: () => void;
    className?: string;
}

const Step = ({
    step,
    icon: Icon,
    title,
    description,
    tone,
    stepLabel,
}: {
    step: number;
    icon: typeof Search;
    title: string;
    description: string;
    tone: string;
    /** Pre-rendered "Step 1" label, so the word is translated per language. */
    stepLabel: string;
}) => (
    /* A step is a column of the empty-state surface, not a card of its own:
       three bordered, shadowed tiles inside a bordered surface read as glass
       stacked on glass. Rules carry the separation instead. */
    <div className="flex items-start gap-3 border-t border-stone-200/70 pt-4 first:border-t-0 first:pt-0 sm:border-t-0 sm:border-l sm:pl-4 sm:pt-0 sm:first:border-l-0 sm:first:pl-0 dark:border-stone-800">
        <div className={cn("shrink-0 h-9 w-9 rounded-xl flex items-center justify-center", tone)}>
            <Icon className="h-4 w-4" aria-hidden="true" />
        </div>
        <div className="min-w-0 flex-1">
            <p className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">
                {stepLabel}
            </p>
            <p className="text-sm font-semibold text-foreground mt-0.5">{title}</p>
            <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{description}</p>
        </div>
    </div>
);

const HomeEmptyState = ({
    isAuthenticated,
    onBrowseTopics,
    onStartPractice,
    onSignIn,
    className,
}: HomeEmptyStateProps) => {
    const { t } = useTranslation();
    return (
        <div
            className={cn(
                slateCardClass,
                "relative overflow-hidden p-6 sm:p-8",
                "bg-gradient-to-br from-stone-50/60 via-background to-blue-50/30 dark:from-stone-900/40 dark:via-background dark:to-blue-950/10",
                className
            )}
        >
            <div
                className="pointer-events-none absolute inset-0 opacity-[0.35] dark:opacity-20"
                aria-hidden="true"
                style={{
                    backgroundImage:
                        "radial-gradient(circle at 1px 1px, hsl(var(--muted-foreground) / 0.18) 1px, transparent 0)",
                    backgroundSize: "16px 16px",
                    maskImage: "radial-gradient(ellipse at center, black 30%, transparent 70%)",
                    WebkitMaskImage:
                        "radial-gradient(ellipse at center, black 30%, transparent 70%)",
                }}
            />

            <div className="relative max-w-3xl mx-auto text-center space-y-5">
                <div className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider text-primary">
                    <Sparkles className="h-3 w-3" />
                    {t("home.emptyState.eyebrow", "Get started")}
                </div>

                <div>
                    <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-stone-900 dark:text-white">
                        {isAuthenticated
                            ? t("home.emptyState.titleAuthenticated", "Your dashboard is ready")
                            : t("home.emptyState.title", "Start speaking practice in 3 steps")}
                    </h2>
                    <p className="mt-2 text-sm sm:text-base text-stone-500 dark:text-stone-400 max-w-2xl mx-auto">
                        {isAuthenticated
                            ? t("home.emptyState.descriptionAuthenticated", "Pick a topic below to start your first session. We'll track your progress and build a streak from there.")
                            : t("home.emptyState.description", "Create an account, pick a topic, and start a 5-minute speaking session. No card required.")}
                    </p>
                </div>

                <div className="grid grid-cols-1 gap-0 text-left pt-2 sm:grid-cols-3">
                    <Step
                        step={1}
                        stepLabel={t("home.emptyState.stepLabel", { step: 1 })}
                        icon={isAuthenticated ? Compass : Search}
                        title={t("home.emptyState.steps.browse", "Browse topics")}
                        description={t("home.emptyState.steps.browseDescription", "Hundreds of curated topics across business, travel, lifestyle, and more.")}
                        tone="bg-primary/10 text-primary"
                    />
                    <Step
                        step={2}
                        stepLabel={t("home.emptyState.stepLabel", { step: 2 })}
                        icon={Flame}
                        title={t("home.emptyState.steps.pick", "Pick a topic")}
                        description={t("home.emptyState.steps.pickDescription", "Filter by category, level, or popularity. Save favorites to your library.")}
                        tone="bg-orange-500/15 text-orange-600 dark:text-orange-400"
                    />
                    <Step
                        step={3}
                        stepLabel={t("home.emptyState.stepLabel", { step: 3 })}
                        icon={Mic}
                        title={t("home.emptyState.steps.practise", "Practice speaking")}
                        description={t("home.emptyState.steps.practiseDescription", "Record yourself answering questions. Build a streak and track progress over time.")}
                        tone="bg-blue-500/15 text-blue-600 dark:text-blue-400"
                    />
                </div>

                <div className="flex flex-wrap items-center justify-center gap-2 pt-2">
                    {isAuthenticated ? (
                        <>
                            {onBrowseTopics && (
                                <Button
                                    size="sm"
                                    onClick={onBrowseTopics}
                                    className={cn(primaryCtaClass, "w-auto px-5 h-10")}
                                >
                                    <Compass className="w-4 h-4 mr-1.5" />
                                    {t("home.hero.browseTopics", "Browse Topics")}
                                </Button>
                            )}
                            {onStartPractice && (
                                <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={onStartPractice}
                                    className="w-auto px-5 h-10 rounded-lg"
                                >
                                    <Mic className="w-4 h-4 mr-1.5" />
                                    {t("home.emptyState.quickPractice", "Quick Practice")}
                                </Button>
                            )}
                        </>
                    ) : (
                        <>
                            {onSignIn && (
                                <Button
                                    size="sm"
                                    onClick={onSignIn}
                                    className={cn(primaryCtaClass, "w-auto px-5 h-10")}
                                >
                                    {t("home.emptyState.getStartedFree", "Get started free")}
                                </Button>
                            )}
                            {onBrowseTopics && (
                                <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={onBrowseTopics}
                                    className="w-auto px-5 h-10 rounded-lg"
                                >
                                    <Compass className="w-4 h-4 mr-1.5" />
                                    {t("home.emptyState.cta", "Browse topics")}
                                </Button>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export { HomeEmptyState };
export type { HomeEmptyStateProps };
